import type { ReactNode } from "react";

/**
 * One pattern for "no data yet", "loading", and "something failed". Pages
 * import these instead of hand-rolling phrases like "No calls." / "No
 * calls yet — waiting for activity." / silence.
 *
 * Visual size is controlled via the `size` prop:
 *   - "inline" — short, single line, fits inside a row or list
 *   - "block"  — vertically padded, fits in a pane
 */

type Size = "inline" | "block";

export function EmptyState({
  id,
  title,
  hint,
  action,
  size = "block",
}: {
  id?: string;
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
  size?: Size;
}) {
  return (
    <div id={id} className={`state state-empty state-${size}`}>
      <div className="state-title">{title}</div>
      {hint && <div className="state-hint">{hint}</div>}
      {action && <div className="state-action">{action}</div>}
    </div>
  );
}

export function LoadingState({
  id,
  label = "Loading…",
  size = "block",
}: {
  id?: string;
  label?: string;
  size?: Size;
}) {
  return (
    <div id={id} className={`state state-loading state-${size}`} role="status" aria-live="polite">
      <span className="state-spinner" aria-hidden />
      <span className="state-title">{label}</span>
    </div>
  );
}

export function ErrorState({
  id,
  message,
  onRetry,
  size = "block",
}: {
  id?: string;
  message: string;
  onRetry?: () => void;
  size?: Size;
}) {
  return (
    <div id={id} className={`state state-error state-${size}`} role="alert">
      <div className="state-title">Something went wrong</div>
      <div className="state-hint">{message}</div>
      {onRetry && (
        <button id={id ? `${id}-retry` : undefined} type="button" className="state-retry" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}
